import React from 'react';
import { User, Mail, Phone, MapPin, Clock, BookOpen, Building, Target, Star } from 'lucide-react';
import { SchoolTeacher } from '../types';

interface SchoolTeacherDetailProps {
  teacher: SchoolTeacher;
}

const SchoolTeacherDetail: React.FC<SchoolTeacherDetailProps> = ({ teacher }) => {
  const getInstructorTypeLabel = (type: string) => {
    switch (type) {
      case 'fulltime': return 'Giảng viên';
      case 'parttime': return 'Kiêm nhiệm';
      case 'guest': return 'Thỉnh giảng';
      default: return type;
    }
  };

  const getInstructorTypeColor = (type: string) => {
    switch (type) {
      case 'fulltime': return 'bg-blue-100 text-blue-800';
      case 'parttime': return 'bg-yellow-100 text-yellow-800';
      case 'guest': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'on_leave':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Không hoạt động';
      case 'on_leave':
        return 'Nghỉ phép';
      default:
        return status;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{teacher.name}</h2>
            <p className="text-gray-600">{teacher.position || 'Giảng viên'}</p>
            <div className="flex items-center gap-4 mt-2">
              <span className={`px-2 py-1 text-xs rounded-full ${getInstructorTypeColor(teacher.instructorType || 'fulltime')}`}>
                {getInstructorTypeLabel(teacher.instructorType || 'fulltime')}
              </span>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(teacher.status)}`}>
                {getStatusText(teacher.status)}
              </span>
            </div>
          </div>
        </div>
        {teacher.code && (
          <div className="text-right">
            <p className="text-sm text-gray-500">Mã giáo viên</p>
            <p className="text-lg font-mono font-semibold text-gray-900">{teacher.code}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="lg:col-span-2">
          <div className="space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin liên hệ</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900">{teacher.email}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Điện thoại</p>
                    <p className="text-gray-900">{teacher.phone}</p>
                  </div>
                </div>
                {teacher.unit && (
                  <div className="flex items-center">
                    <Building className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Đơn vị</p>
                      <p className="text-gray-900">{teacher.unit}</p>
                    </div>
                  </div>
                )}
                {teacher.location && (
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Địa điểm</p>
                      <p className="text-gray-900">{teacher.location}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Teaching Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin giảng dạy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Quota tháng</p>
                    <p className="text-gray-900">{teacher.monthlyQuota || 0} giờ</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Target className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Mức giá/giờ</p>
                    <p className="text-gray-900">{teacher.hourlyRate?.toLocaleString('vi-VN') || 0} VNĐ</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Tổng lớp dạy</p>
                    <p className="text-gray-900">{teacher.totalClasses || 0}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Đánh giá</p>
                    <p className="text-gray-900">{teacher.rating || 0}/5.0</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subjects */}
            {teacher.subjects && teacher.subjects.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Môn dạy</h3>
                <div className="flex flex-wrap gap-2">
                  {teacher.subjects.map((subject) => (
                    <span key={subject} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            {teacher.skills && teacher.skills.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kỹ năng</h3>
                <div className="flex flex-wrap gap-2">
                  {teacher.skills.map((skill) => (
                    <span key={skill} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {teacher.certifications && teacher.certifications.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Chứng chỉ</h3>
                <div className="flex flex-wrap gap-2">
                  {teacher.certifications.map((cert) => (
                    <span key={cert} className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Class Assignment */}
          {teacher.className && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Lớp học được phân</h4>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Tên lớp</p>
                  <p className="text-gray-900 font-medium">{teacher.className}</p>
                </div>
                {teacher.grade && (
                  <div>
                    <p className="text-sm text-gray-500">Khối</p>
                    <p className="text-gray-900">{teacher.grade}</p>
                  </div>
                )}
                {teacher.schoolYear && (
                  <div>
                    <p className="text-sm text-gray-500">Năm học</p>
                    <p className="text-gray-900">{teacher.schoolYear}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* School Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Thông tin trường</h4>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">Tên trường</p>
                <p className="text-gray-900 font-medium">{teacher.schoolName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Mã trường</p>
                <p className="text-gray-900 font-mono">{teacher.schoolId}</p>
              </div>
            </div>
          </div>

          {/* Performance Stats */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Thống kê hiệu suất</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Đánh giá TB</span>
                <span className="font-semibold text-gray-900">{teacher.rating || 0}/5.0</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tổng lớp dạy</span>
                <span className="font-semibold text-gray-900">{teacher.totalClasses || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Quota tháng</span>
                <span className="font-semibold text-gray-900">{teacher.monthlyQuota || 0}h</span>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Thông tin hệ thống</h4>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">Ngày tạo</p>
                <p className="text-gray-900 text-sm">
                  {new Date(teacher.createdAt).toLocaleDateString('vi-VN')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cập nhật lần cuối</p>
                <p className="text-gray-900 text-sm">
                  {new Date(teacher.updatedAt).toLocaleDateString('vi-VN')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolTeacherDetail;
